import { StatusEnum } from '@/types/cart';
import { getRemainingCount } from '@/utils/common/product-card';
import { isValidArray } from '@/utils/isValidArray';
import checkProductCardShowMkplSection from '@/utils/mkpl/checkProductCardShowMkplSection';
import React, { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { CardType, ProductCardProps } from '../../types';
import CardBodyPreSale from './card-body-pre-sale';
import CardBodyPredictDeliveryDate from './card-body-predict-delivery-date';
import { arrayToObject } from '../../helper/get-product-infos';
import { ProductTags } from '../../constants';
import dynamic from 'next/dynamic';

const WeeklySold = dynamic(() => import('../weeklySold'));
const RemainCount = dynamic(() => import('../remainCount'));
const ProductTag = dynamic(() => import('../product-tag'));
const ShipsFrom = dynamic(() => import('./card-body-mkpl-ships'));
const MkplEta = dynamic(() => import('./card-body-mkpl-eta'));
const LinkedTag = dynamic(() => import('./card-body-linked-tag'));

const CardBodyBottomInfo: React.FC<Partial<ProductCardProps & CardType>> = props => {
  const {
    showAllTags = false,
    showActivity = true,
    activityTagLimit = null,
    showSoldBy = true,
    showLeft = true,
    showWeekly = true,
    showRankTag = true,
    data,
    className,
    cardType = 'vertical',
    productTagClassNames,
    mainBottomContentCls = ''
  } = props;
  const remainCount = getRemainingCount(data);
  const weekSoldCount = data?.sold_count_ui !== '0' ? data?.sold_count_ui : ''; // 格式化显示的周销量

  const rankData = data?.entrance_tag;

  const isSoldOut = useMemo(() => {
    return data?.sold_status === StatusEnum?.sold_out;
  }, [data]);

  // 获取产品标签数据，如果卡片类型为横版，则将周销量标签添加到产品标签数据中
  const productTagsData = useMemo(() => {
    if (cardType !== 'horizontal' || !showWeekly || !weekSoldCount) return data?.product_tag_list || [];
    const weekFeaturedTag = {
      tag_key: 'weekly_sold',
      tag_name: (data?.sold_count_ui).toUpperCase(),
      tag_color: 'rgba(238,242,251,1)',
      tag_font_color: 'rgba(7,16,26,1)',
      tag_icon_url: null,
      tag_position: null,
      tag_type: 'weekly_sold'
    };
    return [weekFeaturedTag, ...(data?.product_tag_list || [])];
  }, [cardType, data?.product_tag_list, data?.sold_count_ui, showWeekly, weekSoldCount]);

  // 判断标签的展示，最多展示两行
  // 优先级：activity, ships_from, topx, eta, 周销量 & 剩余库存
  // 优先级：activity tag优先级hot deal > points back > promo > bogo
  const showMap = useMemo(() => {
    const { showShipsFrom, showEta } = checkProductCardShowMkplSection(data, activityTagLimit);

    // Tips: conditions的顺序是优先级的顺序
    const conditions = [
      { key: ProductTags.DELIVERY_DATE, show: cardType === 'vertical' && !!data?.predict_delivery_date_ui },
      { key: ProductTags.PRE_SALE, show: cardType === 'vertical' && !!data?.is_presale && !isSoldOut },
      { key: ProductTags.ACTIVITY, show: showActivity && isValidArray(data?.product_tag_list) },
      { key: ProductTags.SHIPS_FROM, show: cardType === 'vertical' && showSoldBy && showShipsFrom },
      {
        key: ProductTags.RANKING,
        show: cardType === 'vertical' && (showAllTags ? !!rankData : showRankTag && !!rankData)
      },
      { key: ProductTags.ETA, show: cardType === 'vertical' && showSoldBy && showEta },
      { key: ProductTags.LEFT, show: showAllTags ? !!remainCount : showLeft && !!remainCount },
      { key: ProductTags.WEEKLY_SOLD, show: cardType === 'vertical' && showWeekly && !!weekSoldCount }
    ];

    if (!showAllTags) {
      const shownItems = conditions.filter(condition => condition.show);
      if (shownItems.length >= 2) {
        return arrayToObject(shownItems.slice(0, 2));
      }
    }

    return arrayToObject(conditions.filter(condition => condition.show));
  }, [
    activityTagLimit,
    cardType,
    data,
    isSoldOut,
    rankData,
    remainCount,
    showActivity,
    showAllTags,
    showLeft,
    showRankTag,
    showSoldBy,
    showWeekly,
    weekSoldCount
  ]);

  const displayItems = useMemo(() => {
    // Tips: display2Items 的顺序是DOM展示的顺序
    let display2Items: any = [
      showMap[ProductTags.PRE_SALE] && <CardBodyPreSale {...props} />,
      showMap[ProductTags.ACTIVITY] && (
        <ProductTag
          key="productTag"
          {...props}
          classNames={productTagClassNames}
          tagClassName="mb-1"
          cardType={cardType}
          data={{
            ...data,
            product_tag_list: productTagsData
          }}
        />
      ),
      showMap[ProductTags.LEFT] && <RemainCount key="remainCount" {...props} />,
      showMap[ProductTags.WEEKLY_SOLD] && <WeeklySold key="WeeklySold" {...props} />,
      showMap[ProductTags.ETA] && <MkplEta key="mkplEta" className="mt-0.5" {...props} />,
      showMap[ProductTags.SHIPS_FROM] && <ShipsFrom key="shipsFrom" {...props} />,
      showMap[ProductTags.RANKING] && <LinkedTag key="RankTag" {...props} />,
      showMap[ProductTags.DELIVERY_DATE] && (
        <CardBodyPredictDeliveryDate className="my-0.5" key="CardBodyPredictDeliveryDate" {...props} />
      )
    ];
    return display2Items;
  }, [cardType, data, productTagClassNames, productTagsData, props, showMap]);

  if (isValidArray(displayItems)) {
    return (
      <div
        data-testid="wid-product-card-bottom-tags"
        className={twMerge('mt-1.5 flex flex-col justify-start items-start', className, mainBottomContentCls)}>
        {displayItems.map((el, idx) => (
          <React.Fragment key={`disp-items-${idx}`}>{el || null}</React.Fragment>
        ))}
      </div>
    );
  }
  return null;
};

export default CardBodyBottomInfo;
